import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";
import { FaUserCircle, FaQuestionCircle, FaSignOutAlt } from "react-icons/fa";
import { NavLink, useLocation, matchPath } from "react-router-dom";
import HelpModal from "./HelpModal"; // Importe o modal
import { helpContent } from "../../config/helpContent";

const TopbarWrapper = styled.div`
    height: 60px;
    background-color: ${({ theme }) => theme.colors.white};
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0 ${({ theme }) => theme.spacing.large};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    position: sticky;
    top: 0;
    z-index: 999;
`;

const ProfileName = styled.span`
    font-weight: bold;
    color: ${({ theme }) => theme.colors.primary};
`;

const ProfileLink = styled(NavLink)`
    font-weight: bold;
    color: ${({ theme }) => theme.colors.primary};
    margin-left: 10px;
    padding: 8px;
    border-radius: 4px;

    &.active {
        background-color: #f0f0f0;
    }
`;

const Topbar = () => {
    const { user, logout } = useAuth();
    const location = useLocation(); // Hook para pegar a localização atual
    const [isHelpOpen, setIsHelpOpen] = useState(false); // Estado para o modal
    const [currentHelp, setCurrentHelp] = useState({ title: "", content: "" }); // Estado para o conteúdo


    const getHelpKey = useCallback(() => {
        const pathname = location.pathname;

        // Lista de TODOS os padrões de rota que queremos reconhecer
        const patterns = [
            // 1. Rotas mais específicas primeiro (com mais segmentos)
            "/admin/projeto/:projectId/etapa/:etapaId/documentos",
            "/employee/projeto/:projectId/etapa/:etapaId/documentos",
            "/client/projeto/:projectId/etapa/:etapaId/documentos",
            "/employee/projeto/:projectId/editar-etapas",
            "/employee/projeto/:projectId/editar",
            "/admin/usuarios/editar/:userId",

            // 2. Rotas de detalhe de projeto (genéricas por ID)
            "/admin/projeto/:projectId",
            "/client/projeto/:projectId",
            "/employee/projeto/:projectId",

            // 3. Rotas exatas e de Perfil (específicas por role)
            "/admin/perfil",
            "/employee/perfil",
            "/client/perfil",
            "/employee/inicio",
            "/employee/pesquisa",
            "/employee/novo-projeto",
            "/employee/novo-projeto/etapas",
            "/client/inicio",
            "/client/pesquisa",
            "/admin/inicio",
            "/admin/pesquisa",
            "/admin/usuarios",
            "/admin/usuarios/novo",
        ];

        for (const pattern of patterns) {
            // Tenta dar match no padrão com a URL atual
            const match = matchPath({ path: pattern, end: true }, pathname);

            if (match) {
                // Se deu match, agora decidimos qual chave de conteúdo retornar

                // CASO 1: É uma rota de documentos
                if (
                    pattern.includes("/etapa/") &&
                    pattern.endsWith("/documentos")
                ) {
                    // Mapeia para a chave genérica '/documentos'
                    if (helpContent["/documentos"]) {
                        return "/documentos";
                    }
                }

                // CASO 2: É uma rota de detalhe de projeto (sem /editar ou /etapa)
                if (
                    pattern.includes("/:projectId") &&
                    !pattern.includes("/etapa/") &&
                    !pattern.includes("/editar")
                ) {
                    // Mapeia para a chave genérica por role (ex: '/admin/projeto')
                    const role = pathname.split("/")[1]; // Pega admin, client ou employee
                    const genericProjectKey = `/${role}/projeto`;
                    if (helpContent[genericProjectKey]) {
                        return genericProjectKey;
                    }
                }

                // CASO 3: É uma rota exata ou específica (ex: '/admin/perfil', '/employee/projeto/:projectId/editar')
                // Verifica se o próprio padrão (pattern) existe como chave no helpContent
                if (helpContent[pattern]) {
                    return pattern;
                }

                // Se encontrou um match mas nenhuma chave de conteúdo foi encontrada,
                // paramos aqui para não cair no fallback (ex: /admin/projeto/123 deu match, mas /admin/projeto não existe)
                break;
            }
        }

        // FALLBACK: Se o loop terminar sem NENHUM matchPath
        // Tenta a chave genérica de projeto (caso a URL seja /admin/projeto/ID e o padrão :projectId não tenha sido encontrado)
        const pathSegments = pathname.split("/").filter(Boolean);
        if (pathSegments.length === 3 && pathSegments[1] === "projeto") {
            const genericKey = `/${pathSegments[0]}/projeto`;
            if (helpContent[genericKey]) return genericKey;
        }

        return null; // Nenhuma chave correspondente encontrada
    }, [location.pathname, helpContent]); // Adicionado helpContent às dependências

    // Atualiza o conteúdo da ajuda quando a rota muda
    useEffect(() => {
        const key = getHelpKey();
        console.log("Help key determined:", key); // Log para depuração
        if (key && helpContent[key]) {
            setCurrentHelp(helpContent[key]);
        } else {
            // Define um conteúdo padrão ou vazio se não encontrar
            setCurrentHelp({
                title: "Ajuda",
                content: "Conteúdo de ajuda não encontrado para esta página.",
            });
        }
    }, [getHelpKey]); // Recalcula quando a função getHelpKey (e consequentemente location.pathname) muda

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "F1") {
                event.preventDefault(); // Impede a ajuda padrão do navegador
                setIsHelpOpen(true);
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        // Limpa o listener quando o componente desmonta
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []); // Array vazio significa que roda apenas na montagem e desmontagem

    const profilePath = `/${user?.role}/perfil`;

    return (
        <>
            {" "}
            {/* Fragmento para envolver Topbar e Modal */}
            <TopbarWrapper>
                {/* Botão de Ajuda adicionado à esquerda dos dados do perfil */}
                <HelpButton onClick={() => setIsHelpOpen(true)}>
                    <FaQuestionCircle size={22} />
                </HelpButton>

                <ProfileMenu>
                    <FaUserCircle size={24} color="#800020" />
                    <ProfileName>
                        {user?.name ||
                            user?.razaoSocial ||
                            user?.tradeName ||
                            "Usuário"}
                    </ProfileName>

                    {user &&
                        profilePath && ( // Verifica se user e profilePath existem
                            <ProfileLink to={profilePath}>
                                Meu Perfil
                            </ProfileLink>
                        )}

                    <LogoutButton onClick={logout}>
                        <FaSignOutAlt style={{ marginRight: "5px" }} /> Sair
                    </LogoutButton>
                </ProfileMenu>
            </TopbarWrapper>
            {/* Renderiza o Modal */}
            <HelpModal
                isOpen={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
                title={currentHelp.title}
                content={currentHelp.content}
            />
        </>
    );
};

// Adicione os estilos para o botão de ajuda e ajuste o ProfileMenu se necessário
const HelpButton = styled.button`
    margin-right: auto; /* Empurra o botão para a esquerda */
    padding: 8px;
    color: #666;
    &:hover {
        color: ${({ theme }) => theme.colors.primary};
    }
`;

// Ajuste no ProfileMenu para não ocupar todo o espaço
const ProfileMenu = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.small};
`;

// Estilo para o botão de Logout
const LogoutButton = styled.button`
    display: flex;
    align-items: center;
    font-weight: bold;
    color: #555;
    margin-left: 15px; /* Ajuste a margem se necessário */
    padding: 8px;
    &:hover {
        color: ${({ theme }) => theme.colors.primary};
    }
`;

export default Topbar; // Mova export default para o final
